import { useState, type ReactNode } from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { CheckIcon } from "@phosphor-icons/react/Check";
import { MinusIcon } from "@phosphor-icons/react/Minus";
import { Icon } from "../Icons/Icon";
import { Tooltip } from "../Feedback/Tooltip";
import { useMarkDialogDirty } from "../Dialog/FormDirtyContext";
import { ConfirmDialog } from "../Dialog/Internal/ConfirmDialog";
import { toneIconColors } from "./Internal/buttonStyles";
import { useControlPresentation } from "./Internal/useControlPresentation";
import { resolveConfirmConfig, type ConfirmConfig } from "./confirmConfig";

interface CheckboxProps {
  label: ReactNode;
  checked: boolean;
  /** Tri-state: shows a dash icon and distinct styling. Ignored when `checked` is true. */
  indeterminate?: boolean | undefined;
  disabled?: boolean;
  /** Optional help text shown via an info-icon tooltip next to the label. */
  tooltip?: string;
  /** Secondary line under the label, explaining what the option means. */
  description?: ReactNode;
  onChange: (checked: boolean) => void;
  /**
   * Put a confirmation dialog in front of the toggle — `onChange` only fires
   * once the user confirms. Same shape as Button's `confirm`.
   *
   * It gates the toggle in BOTH directions, so to confirm only one of them
   * (e.g. only when switching a recommended option off) pass it conditionally
   * on `checked`.
   */
  confirm?: string | ConfirmConfig | undefined;
  /** Forwarded to the underlying checkbox element for test selection. */
  "data-testid"?: string | undefined;
}

export function Checkbox({
  label,
  checked,
  indeterminate,
  disabled,
  tooltip,
  description,
  onChange,
  confirm,
  "data-testid": dataTestid,
}: CheckboxProps) {
  const markDirty = useMarkDialogDirty();
  // "underlined" is the presentation axis' name for the Cargo look, not a
  // literal underline (see CLAUDE.md). Cargo's checkbox — the booking form's
  // special-services rows — has no border: the label and description sit left,
  // a large square box sits right, and hovering previews the check in a lighter
  // brand. The dense admin default is the compact box-first row instead.
  const underlined = useControlPresentation() === "underlined";
  const [hover, setHover] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const state = checked ? true : indeterminate ? "indeterminate" : false;
  const accent =
    "data-[state=checked]:border-brand data-[state=checked]:bg-brand data-[state=indeterminate]:border-brand data-[state=indeterminate]:bg-brand";

  const confirmConfig = resolveConfirmConfig(confirm);

  function requestChange(next: boolean) {
    markDirty?.();
    if (confirmConfig) {
      setPending(next);
      setConfirmOpen(true);
      return;
    }
    onChange(next);
  }

  const onCheckedChange = (val: boolean | "indeterminate") => {
    // Any click on an indeterminate checkbox resolves to checked,
    // mirroring the standard "select all" UX.
    requestChange(val === true || val === "indeterminate");
  };
  const Glyph = state === "indeterminate" ? MinusIcon : CheckIcon;

  const cursor = disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer";

  // Tooltip rendered as a sibling of the <label>, not a child, so:
  //   1. clicking the info icon doesn't toggle the checkbox
  //   2. its visibility doesn't get dimmed by the disabled <label>'s opacity
  function finish_(row: ReactNode) {
    const withTooltip = tooltip ? (
      <span className="inline-flex items-center gap-2">
        {row}
        <Tooltip content={tooltip} variant="info" />
      </span>
    ) : (
      row
    );

    if (!confirmConfig) {
      return withTooltip;
    }

    return (
      <>
        {withTooltip}
        <ConfirmDialog
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          onConfirm={() => {
            onChange(pending);
            setConfirmOpen(false);
          }}
          title={confirmConfig.title}
          message={confirmConfig.message}
          confirmLabel={confirmConfig.confirmLabel ?? "Confirm"}
          cancelLabel={confirmConfig.cancelLabel}
          confirmTone={confirmConfig.iconTone ?? "neutral"}
          icon={confirmConfig.icon}
          iconColors={toneIconColors[confirmConfig.iconTone ?? "neutral"]}
        />
      </>
    );
  }

  if (underlined) {
    // Cargo draws an empty square and puts the check INSIDE it in brand,
    // rather than filling the box — so an unchecked box stays a plain outline
    // and hovering shows a lighter preview of the check you're about to make.
    const cargoBox = (
      <CheckboxPrimitive.Root
        {...(dataTestid !== undefined && { "data-testid": dataTestid })}
        checked={state}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
        className="flex h-8 w-8 shrink-0 items-center justify-center border border-border bg-card data-[disabled]:cursor-not-allowed"
      >
        {/* The answer is a heavy brand check; the hover preview is the same
            mark in faint grey. Both used to be bold and both blue — brand
            against light-blue — which at this size was the same picture, so
            you couldn't tell a ticked box from one under the cursor. */}
        {checked || indeterminate ? (
          <Icon icon={Glyph} size="lg" color="text-brand-text" weight="bold" />
        ) : (
          hover &&
          !disabled && (
            <Icon
              icon={CheckIcon}
              size="lg"
              color="text-content-faint"
              weight="bold"
            />
          )
        )}
      </CheckboxPrimitive.Root>
    );

    const row = (
      // Bottom padding only, so the first row's label starts level with a
      // field in the neighbouring column rather than 8px below it. The rows
      // are spaced by that padding, matching Cargo's accessorial list.
      <label
        className={`group flex w-full items-center justify-between gap-4 pb-4 ${cursor}`}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <span className="flex min-w-0 flex-col">
          {/* Plain text flow. This used to be an `inline-flex items-center
              gap-1` to stop a trailing info bubble riding up on the baseline —
              Tooltip aligns and spaces its own mark now, so the flex only
              double-spaced it and stopped a long label wrapping. */}
          <span className="text-base font-normal text-foreground">{label}</span>
          {description && (
            <span className="text-xs font-light text-muted-foreground">
              {description}
            </span>
          )}
        </span>
        {cargoBox}
      </label>
    );

    return finish_(row);
  }

  const box = (
    <CheckboxPrimitive.Root
      {...(dataTestid !== undefined && { "data-testid": dataTestid })}
      checked={state}
      disabled={disabled}
      onCheckedChange={onCheckedChange}
      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 border-border bg-card data-[disabled]:cursor-not-allowed ${accent}`}
    >
      <CheckboxPrimitive.Indicator>
        <Icon icon={Glyph} size="sm" color="text-white" weight="regular" />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );

  const text = (
    <span className="flex min-w-0 flex-col gap-0.5">
      <span className="text-sm font-bold text-foreground">{label}</span>
      {description && (
        <span className="text-xs font-normal text-muted-foreground">
          {description}
        </span>
      )}
    </span>
  );

  // With a description the box top-aligns against the label line; a plain
  // single-line checkbox centers box and text on the same axis (the top-align
  // + nudge combination leaves the text sitting 2px high).
  return finish_(
    description ? (
      <label className={`group flex items-start gap-3 py-2 ${cursor}`}>
        <span className="mt-0.5">{box}</span>
        {text}
      </label>
    ) : (
      <label className={`group flex items-center gap-3 py-2 ${cursor}`}>
        {box}
        {text}
      </label>
    ),
  );
}
